import { TranslationKey } from './../../../../../../../types/translation/TranslationKey';
import { Grid } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import ProcessOrderTransactionDefaultBody from './ProcessOrderTransactionDefaultBody';
import {
  ProductTransaction,
  ProductTransactionProcessType,
  ProductTransactionType,
} from '../../../../../../../types/productTransaction';
import { ProductMasterData } from '../../../../../../../types/product_master_data/productMasterData';
import { Container } from '../../../../../../../types/container';
import { CompanyContext } from '../../../../../../../context/CompanyContext';
import ToggleButton from '../../../../../Common/ToggleButton';
import { t } from '../../../../../../../types/translation/Translator';
import ProcessOrderTransactionAssetBody from './ProcessOrderTransactionAssetBody';
import { CompanyRoleAssignmentContext } from '../../../../../../../context/CompanyRoleAssignmentContext';
import { CompanyRole } from '../../../../../../../types/company_role_assignment';
import { ProductContext } from '../../../../../../../context/ProductContext';

export enum ProcessOrderTransactionState {
  default = 'default',
  //    container, TODO: Split container away from default
  asset = 'asset',
}

interface ProcessOrderTransactionBodyProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  userId?: string;
  setUserId: (userId?: string) => void;

  disabled?: boolean;
}

export default function ProcessOrderTransactionBody({
  transaction,
  setTransaction,
  productMasterData,
  container,
  userId,
  setUserId,
  disabled = false,
}: ProcessOrderTransactionBodyProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { products } = useContext(ProductContext);

  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);

  const defaultState = () => {
    if (!disabled) return ProcessOrderTransactionState.default;

    switch (transaction.processType) {
      case ProductTransactionProcessType.asset:
        return ProcessOrderTransactionState.asset;
      case ProductTransactionProcessType.product:
        return ProcessOrderTransactionState.default;
    }
  };

  const [state, setState] = useState<ProcessOrderTransactionState>(defaultState());

  const content = useMemo(() => {
    switch (state) {
      case ProcessOrderTransactionState.default:
        return (
          <ProcessOrderTransactionDefaultBody
            transaction={transaction}
            setTransaction={setTransaction}
            productMasterData={productMasterData}
            container={container}
            disabled={disabled}
            props={{}}
          />
        );
      case ProcessOrderTransactionState.asset:
        if (!productMasterData) return null;
        return (
          <>
            <ProcessOrderTransactionAssetBody
              key={'ProcessOrderTransactionAssetBody'}
              transaction={transaction}
              setTransaction={setTransaction}
              productMasterData={productMasterData}
              userId={userId}
              setUserId={setUserId}
              disabled={disabled}
            />
          </>
        );
    }
  }, [state, transaction, userId, disabled]);

  const stateText = (value: string) => {
    switch (value) {
      case ProcessOrderTransactionState.asset:
        return t(TranslationKey.asset).singular.label;
      case ProcessOrderTransactionState.default:
        return t(TranslationKey.product).singular.label;
    }
    return '';
  };

  const displayAssets =
    currentCompany.settings.featureToggles.assets.inboundOrder && transaction.type === ProductTransactionType.inbound;
  const displayToggle = displayAssets && hasCompanyRole(transaction.companyId, CompanyRole.administrator) && !disabled;

  return (
    <Grid container>
      {displayToggle ? (
        <Grid item xs={12} mb={1}>
          <ToggleButton
            values={[...Object.values(ProcessOrderTransactionState)]}
            selected={state}
            text={stateText}
            onChange={v => {
              setState(v as ProcessOrderTransactionState);

              switch (v) {
                case ProcessOrderTransactionState.default:
                  if (transaction.processType === ProductTransactionProcessType.product) return;
                  setTransaction(transaction => transaction.withProcessType(ProductTransactionProcessType.product));
                  break;
                case ProcessOrderTransactionState.asset:
                  if (transaction.processType === ProductTransactionProcessType.asset) return;
                  setTransaction(transaction => transaction.withProcessType(ProductTransactionProcessType.asset));
                  break;
              }
            }}
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        {content}
      </Grid>
    </Grid>
  );
}
